// components/dashboard/DiscussionEquipe.js - Interface de discussion équipe interne
import React, { useState, useEffect, useRef } from "react";
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  getDocs, 
  Timestamp 
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import { 
  Users, 
  Send, 
  Filter, 
  FileText, 
  MessageSquare,
  Clock,
  Tag
} from "lucide-react";

const DiscussionEquipe = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('tous'); // 'tous', 'general', ou dossierId
  const [selectedDossierForSending, setSelectedDossierForSending] = useState('general');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Couleurs pour les tags des dossiers
  const dossierColors = [
    'bg-blue-100 text-blue-800 border-blue-200',
    'bg-green-100 text-green-800 border-green-200', 
    'bg-yellow-100 text-yellow-800 border-yellow-200',
    'bg-purple-100 text-purple-800 border-purple-200',
    'bg-pink-100 text-pink-800 border-pink-200',
    'bg-indigo-100 text-indigo-800 border-indigo-200',
    'bg-red-100 text-red-800 border-red-200',
    'bg-orange-100 text-orange-800 border-orange-200'
  ];

  const getColorForDossier = (dossierId) => {
    if (!dossierId) return 'bg-gray-100 text-gray-800 border-gray-200';
    const index = Math.abs(dossierId.split('').reduce((a, b) => a + b.charCodeAt(0), 0));
    return dossierColors[index % dossierColors.length];
  };

  // Charger les dossiers actifs
  useEffect(() => {
    const fetchDossiers = async () => {
      try {
        const dossiersQuery = query(
          collection(db, 'dossiers'),
          where('status', 'not-in', ['closed', 'cancelled', 'completed'])
        );
        
        const dossiersSnapshot = await getDocs(dossiersQuery);
        const dossiersData = dossiersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Trier par date de création (plus récents d'abord)
        dossiersData.sort((a, b) => {
          const dateA = a.createdAt?.toDate() || new Date(0);
          const dateB = b.createdAt?.toDate() || new Date(0);
          return dateB - dateA;
        });

        setDossiers(dossiersData);
      } catch (error) {
        console.error('Erreur chargement dossiers:', error);
      }
    };

    fetchDossiers();
  }, []);

  // Écouter les messages en temps réel
  useEffect(() => {
    const messagesQuery = query(
      collection(db, 'team_messages'),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messagesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setMessages(messagesData);
      setLoading(false);
      
      // Scroll vers le bas
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    });

    return () => unsubscribe();
  }, []);

  // Filtrer les messages selon la sélection
  const filteredMessages = messages.filter(message => {
    if (selectedFilter === 'tous') return true;
    if (selectedFilter === 'general') return !message.dossierTag;
    return message.dossierTag === selectedFilter;
  });

  // Compter les messages par dossier
  const getMessageCountForDossier = (dossierId) => {
    return messages.filter(msg => msg.dossierTag === dossierId).length;
  };

  const getGeneralMessageCount = () => {
    return messages.filter(msg => !msg.dossierTag).length;
  };

  // Envoyer un message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const selectedDossier = selectedDossierForSending === 'general' 
        ? null 
        : dossiers.find(d => d.id === selectedDossierForSending);

      const messageData = {
        text: newMessage.trim(),
        createdBy: user.uid,
        createdByName: user.displayName || user.nom || user.email,
        createdByRole: user.role,
        createdAt: Timestamp.now(),
        dossierTag: selectedDossierForSending === 'general' ? null : selectedDossierForSending,
        dossierName: selectedDossier ? (selectedDossier.clientName || selectedDossier.titre) : null,
        type: 'team_internal'
      };

      await addDoc(collection(db, 'team_messages'), messageData);
      setNewMessage('');
      
    } catch (error) {
      console.error('Erreur envoi message:', error);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  // Formater la date
  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (messageDate.getTime() === today.getTime()) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (messageDate.getTime() === today.getTime() - 86400000) {
      return `Hier ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Sidebar des dossiers */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center mb-4">
            <Users className="w-6 h-6 text-blue-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Discussion d'équipe</h2>
          </div>
          <p className="text-sm text-gray-600">
            Communication interne entre les membres de l'équipe
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-3">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Filtres
            </h3>
            
            {/* Filtre Tous */}
            <button
              onClick={() => setSelectedFilter('tous')}
              className={`w-full flex items-center justify-between px-3 py-2 mb-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === 'tous' 
                  ? 'bg-blue-100 text-blue-900 border border-blue-200' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center">
                <Filter className="w-4 h-4 mr-2" />
                <span>🔄 Tous</span>
              </div>
              <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                {messages.length}
              </span>
            </button>

            {/* Filtre Général */}
            <button
              onClick={() => setSelectedFilter('general')}
              className={`w-full flex items-center justify-between px-3 py-2 mb-2 rounded-lg text-sm font-medium transition-colors ${
                selectedFilter === 'general' 
                  ? 'bg-gray-100 text-gray-900 border border-gray-200' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center">
                <MessageSquare className="w-4 h-4 mr-2" />
                <span>💬 Général</span>
              </div>
              <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs">
                {getGeneralMessageCount()}
              </span>
            </button>

            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 mt-6">
              Dossiers actifs
            </h3>
            
            {/* Liste des dossiers */}
            {dossiers.map((dossier) => {
              const messageCount = getMessageCountForDossier(dossier.id);
              const isSelected = selectedFilter === dossier.id;
              
              return (
                <button
                  key={dossier.id}
                  onClick={() => setSelectedFilter(dossier.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 mb-1 rounded-lg text-sm transition-colors ${
                    isSelected 
                      ? 'bg-blue-50 text-blue-900 border border-blue-200' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex-1 text-left">
                    <div className="font-medium truncate">
                      {dossier.clientName || dossier.titre || `Dossier ${dossier.id.slice(0, 8)}`}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {dossier.typeClient} • {dossier.status || 'En cours'}
                    </div>
                  </div>
                  {messageCount > 0 && (
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs ml-2">
                      {messageCount}
                    </span>
                  )}
                </button>
              );
            })}

            {dossiers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucun dossier actif</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Zone de discussion principale */}
      <div className="flex-1 flex flex-col">
        {/* En-tête de la discussion */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedFilter === 'tous' && '🔄 Tous les messages'}
                {selectedFilter === 'general' && '💬 Messages généraux'}
                {selectedFilter !== 'tous' && selectedFilter !== 'general' && (
                  <>
                    📁 {dossiers.find(d => d.id === selectedFilter)?.clientName || 'Dossier sélectionné'}
                  </>
                )}
              </h3>
              <p className="text-sm text-gray-600">
                {filteredMessages.length} message{filteredMessages.length > 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <Clock className="w-4 h-4 mr-1" />
              <span>Temps réel</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
          <div className="space-y-4 max-w-4xl mx-auto">
            {filteredMessages.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun message</h3>
                <p className="text-gray-600">
                  {selectedFilter === 'general' 
                    ? 'Commencez la discussion générale !' 
                    : selectedFilter === 'tous'
                    ? 'Soyez le premier à écrire !'
                    : 'Aucun message pour ce dossier'}
                </p>
              </div>
            ) : (
              filteredMessages.map((message) => (
                <div key={message.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {message.createdByName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-medium text-gray-900">
                        {message.createdByName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatMessageDate(message.createdAt)}
                      </span>
                      {message.dossierTag && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getColorForDossier(message.dossierTag)}`}>
                          <Tag className="w-3 h-3 mr-1" />
                          {message.dossierName}
                        </span>
                      )}
                    </div>
                    <div className="bg-white rounded-lg px-4 py-2 border border-gray-200 shadow-sm">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {message.text}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Zone d'envoi de message */}
        <div className="bg-white border-t border-gray-200 p-4">
          <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
            {/* Sélecteur de dossier */}
            <div className="mb-3">
              <div className="flex items-center space-x-2">
                <Tag className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedDossierForSending}
                  onChange={(e) => setSelectedDossierForSending(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="general">💬 Message général</option>
                  {dossiers.map(dossier => (
                    <option key={dossier.id} value={dossier.id}>
                      📁 {dossier.clientName || dossier.titre || `Dossier ${dossier.id.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Zone de saisie */}
            <div className="flex space-x-3">
              <div className="flex-1">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Tapez votre message..."
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="flex-shrink-0 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>{sending ? 'Envoi...' : 'Envoyer'}</span>
              </button>
            </div>
            
            <div className="mt-2 text-xs text-gray-500">
              Appuyez sur Entrée pour envoyer • Shift+Entrée pour une nouvelle ligne
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DiscussionEquipe;